import { ChangeDetectionStrategy, Component, Input, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import _, { merge, omit } from 'lodash';
import { EMPTY, iif, switchMap, timer } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { SidebarItem } from '../../../../osp-ui/components/sidebar/contract/sidebar-item.contract';
import { SidebarState } from '../../../../osp-ui/components/sidebar/contract/sidebar-state.contract';
import { SidebarComponent } from '../../../../osp-ui/components/sidebar/sidebar.component';
import { OspTemplateDirective } from '../../../../osp-ui/directives/osp-template.directive';
import { EquipmentShortcutService } from '../../../../pages/resources/equipment/services/equipment-shortcut.service';
import { AutomatedTransfersApiService } from '../../../../pages/transfers/automated-transfers/services/automated-transfers-api.service';
import { AutomatedTransfersCountsRefreshService } from '../../../../pages/transfers/automated-transfers/services/automated-transfers-counts-refresh.service';
import { ShortcutListComponent } from '../../../../shared/components/shortcut-list/shortcut-list.component';
import { Authorities } from '../../../../shared/enums/authorities.enum';
import { Modules } from '../../../../shared/enums/modules.enum';
import { IconPipe } from '../../../../shared/pipes/icon.pipe';
import { AccessService } from '../../../../shared/services/access.service';
import { navigateToFirstNestedScreen } from '../../../../shared/utils/navigate-to-first-nested-screen';
import { IconProviderService } from '../../../../whitelabel/services/icon-provider.service';
import { ThemeService } from '../../../../whitelabel/services/theme.service';
import { AuthService } from '../../../services/auth.service';
import { LanguageService } from '../../../services/language.service';
import { SidebarWrapper } from './contracts/sidebar-wrapper.contract';
import { LogoImagePipe } from './pipes/logo-image.pipe';

@Component({
  standalone: true,
  selector: 'osp-sidebar-wrapper',
  templateUrl: './sidebar-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SidebarComponent, TranslateModule, IconPipe, OspTemplateDirective, ShortcutListComponent],
  providers: [LogoImagePipe],
})
export class SidebarWrapperComponent implements SidebarWrapper {
  @Input() registerOnStateChange: ((state: SidebarState) => void) | null = null;

  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly iconProviderService = inject(IconProviderService);
  private readonly themeService = inject(ThemeService);
  private readonly automatedTransfersApiService = inject(AutomatedTransfersApiService);
  private readonly automatedTransfersCountsRefreshService = inject(AutomatedTransfersCountsRefreshService);
  private readonly logoImagePipe = inject(LogoImagePipe);
  private readonly accessService = inject(AccessService);
  private readonly isSuperOrPartnerAdmin = this.authService.isSuperOrPartnerAdmin();
  protected readonly equipmentShortcutService = inject(EquipmentShortcutService);
  protected readonly EQUIPMENT_SUB_CONTAINER_NAME = 'equipment-sidebar';

  protected readonly IconKey = IconKey;
  private readonly EQUIPMENT_OPTION_ID = 'equipments';
  private readonly TRANSFERS_LIST_OPTION_ID = 'transferList';
  private readonly AUTOMATED_TRANSFERS_COUNT_INTERVAL_MS = 2000 * 60; // 2 minutes

  private readonly canSeeAutomatedTransfers =
    !this.isSuperOrPartnerAdmin &&
    this.authService.hasModule(Modules.AUTOMATED_TRANSFERS) &&
    this.authService.hasAuthorities([Authorities.ASSET_FROM_PROJECT_TRANSFER, Authorities.ASSET_FROM_STOCK_TRANSFER]);
  private readonly canSeeResources =
    !this.isSuperOrPartnerAdmin &&
    this.authService.hasAnyAuthority([Authorities.CONTACT_VIEW]) &&
    this.authService.hasModule(Modules.CONTACT_MANAGEMENT);
  private readonly canSeeLocations =
    !this.isSuperOrPartnerAdmin &&
    this.authService.hasAnyAuthority([
      Authorities.PROJECT_VIEW,
      Authorities.PROJECT_ASSIGNEE_VIEW,
      Authorities.STOCK_VIEW,
      Authorities.STOCK_MANAGE,
    ]) &&
    (this.authService.hasModule(Modules.DISPOSITION) || this.authService.hasModule(Modules.BULK_ITEM_MANAGEMENT));

  private readonly $automatedTransfersCounts = toSignal(
    iif(
      () => this.canSeeAutomatedTransfers,
      merge(
        timer(0, this.AUTOMATED_TRANSFERS_COUNT_INTERVAL_MS),
        this.automatedTransfersCountsRefreshService.refresh$,
      ).pipe(switchMap(() => this.automatedTransfersApiService.getAutomatedTransfersCounts())),
      EMPTY,
    ),
  );

  private readonly $transfersListOption = computed<SidebarItem>(() => ({
    id: this.TRANSFERS_LIST_OPTION_ID,
    label: this.translate('modules.navigation.sidebar.transferList'),
    icon: this.iconProviderService.getIcon(IconKey.SidebarTransfers),
    expanded: false,
    visible: this.canSeeAutomatedTransfers,
    badgeValue: this.$automatedTransfersCounts()?.totalCount,
    items: [
      {
        label: this.translate('modules.navigation.sidebar.automatedTransfers.onTheRoad'),
        icon: this.iconProviderService.getIcon(IconKey.SidebarAutomatedTransfersOnTheRoad),
        routerLink: 'automated-transfers/on-the-road',
        routerLinkActiveOptions: { exact: false },
        badgeValue: this.$automatedTransfersCounts()?.onTheRoadEquipmentsCount,
      },
      {
        label: this.translate('modules.navigation.sidebar.automatedTransfers.scheduled'),
        icon: this.iconProviderService.getIcon(IconKey.SidebarAutomatedTransfersScheduled),
        routerLink: 'automated-transfers/scheduled',
        routerLinkActiveOptions: { exact: false },
        badgeValue: this.$automatedTransfersCounts()?.scheduledTransfersCount,
      },
    ],
    command: (event) => navigateToFirstNestedScreen(this.router, event.item),
  }));

  public $sidenavItems = signal<SidebarItem[]>([
    {
      label: this.translate('modules.navigation.sidebar.dashboard'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarDashboard),
      routerLink: '/dashboard',
      visible: !this.isSuperOrPartnerAdmin,
      routerLinkActiveOptions: { exact: false },
    },
    {
      label: this.translate('modules.navigation.sidebar.myfleet'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarMyFleet),
      expanded: false,
      items: [
        {
          id: this.EQUIPMENT_OPTION_ID,
          label: this.translate('modules.navigation.sidebar.equipments'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMyFleet),
          routerLink: 'resources/equipment',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAuthority(Authorities.EQUIPMENT_VIEW),
          subContainerName: this.EQUIPMENT_SUB_CONTAINER_NAME,
          subContainerExpanded: false,
          command: () => this.onEquipmentsMenuClick(),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.resources'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarResources),
      expanded: false,
      visible: this.canSeeResources,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.contacts'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarContacts),
          routerLink: 'resources/contact',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAuthority(Authorities.CONTACT_VIEW) &&
            this.authService.hasModule(Modules.CONTACT_MANAGEMENT),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.locations'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarLocations),
      expanded: false,
      visible: this.canSeeLocations,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.active'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarLocationsActive),
          routerLink: 'locations/active',
          routerLinkActiveOptions: { exact: false },
        },
        {
          label: this.translate('modules.navigation.sidebar.archived'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarLocationsArchived),
          routerLink: 'locations/archived',
          routerLinkActiveOptions: { exact: false },
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.maintenance'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspections),
      expanded: false,
      visible:
        this.authService.hasAnyAuthority([Authorities.MAINTENANCE_TASK_VIEW, Authorities.MAINTENANCE_TASK_MANAGE]) &&
        this.authService.hasModule(Modules.SERVICE),
      items: [
        {
          label: this.translate('modules.navigation.sidebar.maintenanceUpcoming'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspectionsUpcoming),
          routerLink: 'maintenance/upcoming',
        },
        {
          label: this.translate('modules.navigation.sidebar.maintenanceHistory'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarMaintenanceAndInspectionsHistory),
          routerLink: 'maintenance/completed',
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    {
      label: this.translate('modules.navigation.sidebar.reports'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarReporting),
      expanded: false,
      visible:
        !this.authService.isPartnerAdmin() &&
        this.authService.hasAnyAuthority([
          Authorities.REPORT_MANAGE,
          Authorities.REPORT_VIEW,
          Authorities.REPORT_NEXTGEN_READ,
          Authorities.REPORT_NEXTGEN_WRITE,
        ]),
      items: [
        {
          label: this.translate('modules.navigation.sidebar.reportsAvailable'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingAvailable),
          routerLink: 'reports/available',
          routerLinkActiveOptions: { exact: false },
        },
        {
          label: this.translate('modules.navigation.sidebar.reportsAutomatic'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingAutomatic),
          routerLink: 'reports/automatic',
          routerLinkActiveOptions: { exact: false },
          visible: true,
        },
        {
          label: this.translate('modules.navigation.sidebar.reportsEmbedded'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarReportingEmbedded),
          routerLink: 'reports/embedded',
          routerLinkActiveOptions: { exact: false },
          visible:
            !environment.HIDE_INTERACTIVE_REPORTS &&
            this.authService.hasAnyAuthority([Authorities.REPORT_NEXTGEN_READ, Authorities.REPORT_NEXTGEN_WRITE]),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
    { ...this.$transfersListOption() },
    {
      label: this.translate('modules.navigation.sidebar.alarms'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarNotifications),
      routerLink: '/notifications',
      routerLinkActiveOptions: { exact: false },
      visible:
        this.authService.hasAnyAuthority([
          Authorities.NOTIFICATIONS_MANAGE_ALARMS,
          Authorities.NOTIFICATIONS_VIEW_ALARMS,
        ]) && this.authService.hasModule(Modules.ALARMS),
    },
    {
      label: this.translate('modules.navigation.sidebar.administration'),
      icon: this.iconProviderService.getIcon(IconKey.SidebarAdministration),
      expanded: false,
      items: [
        {
          label: this.translate('modules.navigation.sidebar.userAdministration'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationManagement),
          routerLink: 'administration/user-list',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAuthority(Authorities.USER_VIEW),
        },
        {
          label: this.translate('modules.navigation.sidebar.maintenance'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationMaintenanceAndInspections),
          routerLink: 'administration/maintenance',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAuthorities([Authorities.MAINTENANCE_TASK_MANAGE]) &&
            this.authService.hasModule(Modules.SERVICE),
        },
        {
          label: this.translate('modules.navigation.sidebar.management'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarAdministrationManagement),
          routerLink: 'administration/management',
          routerLinkActiveOptions: { exact: false },
          visible: this.authService.hasAnyAuthority([Authorities.CUSTOMER_VIEW, Authorities.ORGANISATION_VIEW]),
        },
        {
          label: this.translate('modules.navigation.sidebar.alarmSettings'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarNotifications),
          routerLink: 'administration/alarmsettings',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAuthority(Authorities.NOTIFICATIONS_MANAGE_DEFAULT_RECIPIENTS) &&
            this.authService.hasModule(Modules.ALARMS),
        },
        {
          label: this.translate('modules.navigation.sidebar.customization'),
          icon: this.iconProviderService.getIcon(IconKey.SidebarCustomization),
          routerLink: 'administration/customization',
          routerLinkActiveOptions: { exact: false },
          visible:
            this.authService.hasAnyAuthority([
              Authorities.EQUIPMENT_STATUS_MANAGE,
              Authorities.ADDITIONAL_FIELD_VIEW,
              Authorities.ADDITIONAL_FIELD_MANAGE,
            ]) || this.accessService.canViewEquipmentType(),
        },
      ],
      command: (event) => navigateToFirstNestedScreen(this.router, event.item),
    },
  ]);

  private readonly $sidebarState = signal<SidebarState>('expanded-fixed');
  private readonly $theme = toSignal(this.themeService.theme$, { initialValue: this.themeService.getCurrentTheme() });

  public readonly $logo = computed(() => {
    const theme = this.$theme();
    const sidebarState = this.$sidebarState();
    return this.logoImagePipe.transform(theme, sidebarState);
  });

  constructor() {
    effect(
      () => {
        const transferOption = this.$transfersListOption();
        this.patchOptionById(omit(transferOption, 'expanded'));
      },
      { allowSignalWrites: true },
    );
  }

  private patchOptionById(updateOption: Partial<SidebarItem>): void {
    this.$sidenavItems.update((values) => {
      return values.map((option) => {
        if (option.id === updateOption.id) {
          return _.merge(option, updateOption);
        }
        return option;
      });
    });
  }

  onStateChange(newState: SidebarState): void {
    this.$sidebarState.set(newState);
    this.registerOnStateChange?.(newState);
  }

  protected translate(key: string): string {
    return this.languageService.getInstant(key);
  }

  private onEquipmentsMenuClick(): void {
    if (this.equipmentShortcutService.$selectedShortcut()) {
      this.equipmentShortcutService.resetToDefaultView();
    }
  }
}
